/**
 * util lib for url query strings with deep nested objects and array
 * based on https://github.com/documentcloud/underscore-contrib/blob/master/underscore.util.strings.js
 *
 */
import { isObject, isString } from "utils/predicates";
import * as predicateUtils from "utils/predicates";
import config from "config";

const plusRegex = /\+/g;
const spaceRegex = /%20/g;
// eslint-disable-next-line no-useless-escape
const bracketRegex = /(?:([^\[]+))|(?:\[(.*?)\])/g;

/**
 * enhances decodeURIComponent by replacing %20 with +
 *
 * @param {string} s
 * @returns {(string|void)} decoded string
 */
function urlDecode(s) {
  if (!s.replace || (!s.replace) instanceof Function) {
    console.error("Value to decode must be string or similar");
    return;
  }
  return decodeURIComponent(s.replace(plusRegex, "%20"));
}

/**
 * enhances encodeURIComponent by replacing %20 with +
 *
 * @param {string} s
 * @returns {string} encoded string
 */
function urlEncode(s) {
  return encodeURIComponent(s).replace(spaceRegex, "+");
}

/**
 * builds query params for given value
 *
 * @param {string} prefix
 * @param {Array|Object} val
 * @param {boolean} top=true value indicating if is top of recursive stack
 * @returns {string} query string
 */
function buildParams(prefix, val, top = true) {
  if (Array.isArray(val)) {
    return val
      .map((value, index) => {
        return buildParams(top ? index : prefix + "[]", value, false);
      })
      .join("&");
  } else if (isObject(val)) {
    let result = [];

    for (let key in val) {
      result.push(
        buildParams(top ? key : prefix + "[" + key + "]", val[key], false),
      );
    }
    return result.join("&");
  }
  return urlEncode(prefix) + "=" + urlEncode(val);
}

/**
 * Checks if the given key is dangerous and should be ignored
 * @param {string} key
 */
function isDangerousKey(key) {
  if (typeof key !== "string") {
    return false;
  }

  switch (key) {
    case "__proto__":
    case "constructor":
    case "prototype":
      return true;
    default:
      return false;
  }
}

/**
 * builds an object based on given query string
 *
 * @param {string} str the search string either with or without the leeding ?
 * @returns {object}
 */
export function fromQuery(str) {
  // Using Object.create(null) is important here, otherwise this can lead to a prototype pollution
  // see https://cornelsen-verlag.atlassian.net/browse/CVSHOP-6902
  let obj = Object.create(null);
  let match;
  let subKey;

  if (!isString(str) || str === "") {
    return obj;
  }
  str = str[0] === "?" ? str.slice(1) : str;
  const parameters = str.split("&");

  for (let parameter of parameters) {
    parameter = parameter.split("=");
    let key = urlDecode(parameter[0]);
    let lastKey = key;
    let depth = obj;

    // Reset so we don't have issues when matching the same string
    bracketRegex.lastIndex = 0;

    // Attempt to extract nested values
    while ((match = bracketRegex.exec(key)) !== null) {
      // fixes prototype pollution, see https://cornelsen-verlag.atlassian.net/browse/CVSHOP-6902 and https://cornelsen-verlag.atlassian.net/browse/CVSHOP-7345
      if (Array.isArray(match)) {
        let shouldExitFlag = false;
        match.forEach((item) => {
          if (isDangerousKey(item)) {
            shouldExitFlag = true;
          }
        });
        if (shouldExitFlag) {
          break;
        }
      }
      if (match[1] !== undefined) {
        // If we're at the top nested level, no new object needed
        subKey = match[1];
      } else {
        // If we're at a lower nested level, we need to step down, and make
        // sure that there is an object to place the value into
        subKey = match[2];
        depth[lastKey] = depth[lastKey] || (subKey ? {} : []);
        depth = depth[lastKey];
      }

      // Save the correct key as a hash or an array
      lastKey = subKey || depth.length;
    }
    depth[lastKey] =
      typeof parameter[1] === "undefined" ? undefined : urlDecode(parameter[1]);
  }

  return obj;
}

/**
 * builds a query stirng based on given obj
 *
 * @param {object} obj
 * @returns {string} the query string
 */
export function toQuery(obj) {
  return buildParams("", obj);
}

/**
 * toFilters
 *
 * @param {String} queryString url query string
 * @returns {Object} filters object literal
 */
export function toFiltersLiteral(queryString) {
  const queryObj = fromQuery(queryString);
  const configFilterNames = Object.keys(config.filters);

  return Object.keys(queryObj)
    .filter((queryParam) => {
      return configFilterNames.some((name) => name === queryParam);
    })
    .reduce((accumulator, paramName) => {
      let values = queryObj[paramName];

      if (!predicateUtils.isArray(values)) {
        if (predicateUtils.isString(values)) {
          values = [values];
        } else {
          throw new TypeError(
            `given value ${values} of filter ${paramName} is not supported`,
          );
        }
      }
      accumulator[paramName] = {
        values: values,
      };
      return accumulator;
    }, {});
}
